import React from "react";
import {
  Ban,
  Landmark,
  BadgeCheck,
  Hourglass,
  Search,
  ClipboardCheck,
  Send,
  PenLine,
  Wallet,
  CircleCheck,
  ClipboardList,
  TriangleAlert,
  Banknote,
  RefreshCw,
  Sparkles,
  ShieldAlert,
} from "lucide-react";
import { AppStatus } from "@/types/borrower";

/**
 * Render the status icon at any size — className controls h/w.
 * Color inherits from CSS `currentColor` so it adapts to its container.
 */
export const getStatusIcon = (status: AppStatus, className = "h-4 w-4"): React.ReactNode => {
  switch (status) {
    case "submitted":
      return <Hourglass className={className} />;
    case "needs_input":
      return <TriangleAlert className={className} />;
    case "ai_analyzing":
      return <Sparkles className={className} />;
    case "funder_review":
      return <Search className={className} />;
    case "contract_ready":
      return <ClipboardCheck className={className} />;
    case "contract_sent":
      return <Send className={className} />;
    case "contract_signed":
      return <PenLine className={className} />;
    case "bank_verification_required":
      return <ShieldAlert className={className} />;
    case "plaid_initiated":
      return <Wallet className={className} />;
    case "plaid_completed":
      return <CircleCheck className={className} />;
    case "final_underwriting":
      return <ClipboardList className={className} />;
    case "funded":
      return <Banknote className={className} />;
    case "repayment":
      return <CircleCheck className={className} />;
    case "defaulted":
      return <TriangleAlert className={className} />;
    case "refi_ready":
      return <RefreshCw className={className} />;
    case "failed":
      return <Ban className={className} />;
    case "declined":
      return <Ban className={className} />;
    case "paid_off":
      // Composite: Landmark + small BadgeCheck overlay
      return (
        <span className="relative inline-flex shrink-0" style={{ fontSize: "inherit" }}>
          <Landmark className={className} />
          <BadgeCheck
            className="absolute"
            style={{ bottom: "-15%", right: "-15%", width: "45%", height: "45%" }}
          />
        </span>
      );
    default:
      return null;
  }
};

interface BadgeConfig {
  label: string;
  color: string;
  icon: React.ReactNode;
}

const iconSm = "h-7 w-7 text-white shrink-0";

export const BADGE_CONFIG: Record<AppStatus, BadgeConfig> = {
  submitted: {
    label: "submitted",
    color: "#2563eb",
    icon: <Hourglass className={iconSm} />,
  },
  needs_input: {
    label: "needs input",
    color: "#eab308",
    icon: <TriangleAlert className={iconSm} />,
  },
  ai_analyzing: {
    label: "ai analyzing",
    color: "#2563eb",
    icon: <Sparkles className={iconSm} />,
  },
  funder_review: {
    label: "under review",
    color: "#2563eb",
    icon: <Search className={iconSm} />,
  },
  contract_ready: {
    label: "contract ready",
    color: "#b45309",
    icon: <ClipboardCheck className={iconSm} />,
  },
  contract_sent: {
    label: "contract sent",
    color: "#7c3aed",
    icon: <Send className={iconSm} />,
  },
  contract_signed: {
    label: "signed",
    color: "#7c3aed",
    icon: <PenLine className={iconSm} />,
  },
  bank_verification_required: {
    label: "verify bank",
    color: "#eab308",
    icon: <ShieldAlert className={iconSm} />,
  },
  plaid_initiated: {
    label: "plaid",
    color: "#b45309",
    icon: <Wallet className={iconSm} />,
  },
  plaid_completed: {
    label: "plaid done",
    color: "#7c3aed",
    icon: <CircleCheck className={iconSm} />,
  },
  final_underwriting: {
    label: "final u/w",
    color: "#2563eb",
    icon: <ClipboardList className={iconSm} />,
  },
  funded: {
    label: "funded",
    color: "#2E7D32",
    icon: <Banknote className={iconSm} />,
  },
  repayment: {
    label: "repayment",
    color: "#2E7D32",
    icon: <CircleCheck className={iconSm} />,
  },
  defaulted: {
    label: "defaulted",
    color: "#C62828",
    icon: <TriangleAlert className={iconSm} />,
  },
  failed: {
    label: "failed",
    color: "#C62828",
    icon: <Ban className={iconSm} />,
  },
  paid_off: {
    label: "paid in full",
    color: "#2E7D32",
    icon: (
      <span className="relative inline-flex text-white">
        <Landmark className="h-7 w-7" />
        <BadgeCheck className="absolute -bottom-0.5 -right-1 h-3.5 w-3.5" />
      </span>
    ),
  },
  refi_ready: {
    label: "re-fi contract sent",
    color: "#ed6c02",
    icon: <RefreshCw className={iconSm} />,
  },
  declined: {
    label: "declined",
    color: "#C62828",
    icon: <Ban className={iconSm} />,
  },
};

interface DealStatusBadgeProps {
  status: AppStatus;
  /** Renders a compact inline pill instead of the large square tile */
  compact?: boolean;
}

const DealStatusBadge: React.FC<DealStatusBadgeProps> = ({ status, compact = false }) => {
  const config = BADGE_CONFIG[status];
  if (!config) return null;

  const { label, color, icon } = config;

  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide"
        style={{
          backgroundColor: `${color}1a`,
          color,
          border: `1px solid ${color}33`,
        }}
      >
        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
        {label}
      </span>
    );
  }

  return (
    <div
      className="flex h-20 w-20 shrink-0 flex-col items-center justify-center gap-1 self-center rounded-lg text-center"
      style={{ backgroundColor: color }}
    >
      {icon}
      <span className="px-0.5 text-[0.65rem] font-semibold leading-tight text-white">{label}</span>
    </div>
  );
};

export default DealStatusBadge;
